import React, { useState } from 'react';
import { Container, Button, TextField, Grid, Box, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import ReCAPTCHA from 'react-google-recaptcha';
import AffiliateAboutBanner from '../../assets/images/americaFlag.jpg';
import Validations from '../../utils/Validations';
import AuthStyles from '../../styles/views/Auth';
import { storeContactUsDetails } from '../../services/Auth';
import { showErrorToastr, showSuccessToastr } from '../../store/actions/toastrActions';
import FAQ from './FAQ';

const useStyles = makeStyles(AuthStyles);

// ---------- AboutUs ----------
const AboutUs = () => (
  <Box>
    <Box my={3} px={{ xs: 2, md: 20 }} pl={{ xs: 2, md: 20 }}>
      <Grid container rowSpacing={1} spacing={2} component={Box}>
        <Grid item xs={12} sm={12} md={6}>
          <Typography
            fontFamily="Lato"
            fontWeight="900"
            sx={{
              display: { xs: 'block', md: 'block' },
              textAlign: { xs: 'center', md: 'left' },
            }}
            variant="h4"
          >
            About Us
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight="400"
            lineHeight="150%"
            letterSpacing="0.5px"
            align="justify"
            pt={1}
          >
            CanAm Excursions is a Canadian-based tour provider committed to providing 
            safe and enjoyable cultural experiences in Punta Cana for Canadian and American 
            travellers. We prioritize safety by partnering with pre-screened tour operators 
            and always have an English and Spanish-speaking representative present to alleviate 
            language barriers for clear communication and prevent you from booking adventures with 
            unprofessional guided tours that may result in theft, accidents, and safety hazards. 
            Keep your loved ones safe by pre-scheduling excursions through us.
          </Typography>
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <Box
            sx={{
              height: { xs: '200px', md: '30vh' },
              maxHeight: '450px !important',
              backgroundImage: `url(${AffiliateAboutBanner})`,
              backgroundRepeat: 'no-repeat',
              backgroundOrigin: 'start',
              backgroundSize: 'cover',
              display: 'block',
              my: 3,
              borderRadius: '40px',
            }}
          />
        </Grid>
      </Grid>
    </Box>
  </Box>
);

// ---------- AboutUsForm ----------
const AboutUsForm = () => {
  const classes = useStyles();
  const { control, handleSubmit } = useForm({ mode: 'onChange' });
  const [captchaError, setCaptchaError] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    captcha: '',
  });
  const handleCaptchaChange = (value) => {
      setFormData({
        ...formData,
        captcha: value,
      });
      setCaptchaError(!value); // Update captchaError based on the presence of value
    };
  const onSubmit = async (data) => {
    try {
      if (!formData.captcha) {
        setCaptchaError(true);
        showErrorToastr('Please complete the captcha.');
        return;
      }
      const toSubmitData = { ...data };
      await storeContactUsDetails(toSubmitData);
      showSuccessToastr('Message sent successfully.');
      window.location.reload();
    } catch ({ response }) {
      showErrorToastr(typeof response === 'string' ? response : 'Something Went Wrong!');
    }
  };

  return (
    <Grid container component={Box}>
      <Grid item xs={12} sm={12} md={6}>
        <FAQ />
      </Grid>

      <Grid item xs={12} sm={12} md={6}>
        <Box
          className={classes.formWrapper}
          sx={{
            background: '#fff',
            minHeight: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
          }}
        >
          <Container maxWidth="sm">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontFamily: 'Lato',
                      fontSize: '1.8rem',
                      mb: 1,
                    }}
                  >
                    Any Questions?
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ ...Validations.REQUIRED }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        required
                        fullWidth
                        label="Name"
                        error={!!error}
                        helperText={error?.message || ''}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{ ...Validations.EMAIL_REQUIRED }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        required
                        fullWidth
                        label="Email address"
                        type="email"
                        error={!!error}
                        helperText={error?.message || ''}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{ validate: matchIsValidTel }}
                    render={({ field, fieldState: { error, invalid } }) => (
                      <MuiTelInput
                        {...field}
                        required
                        fullWidth
                        label="Phone No."
                        error={!!error || invalid}
                        helperText={
                          error || invalid ? error?.message || 'Invalid Phone Number' : ''
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="message"
                    control={control}
                    rules={{ ...Validations.REQUIRED }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        required
                        fullWidth
                        multiline
                        minRows={4}
                        label="Messages"
                        error={!!error}
                        helperText={error?.message || ''}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              {/* Captcha */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center">
                  <ReCAPTCHA
                    sitekey="6LcDFlApAAAAAEtWxNrm3SjzidwvO3OQaCjHJ0DR" // Replace with your reCAPTCHA site key
                    size="normal" // Use 'compact' for a square box
                    onChange={handleCaptchaChange}
                  />
                </Box>
              </Grid>
              {captchaError}
                <Grid item xs={12}>
                  <Box textAlign="center">
                    <Button
                      variant="contained"
                      type="submit"
                      sx={{
                        backgroundColor: '#15B5E8',
                        borderRadius: '30px',
                        paddingX: 5,
                        paddingY: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#00A6D6',
                        },
                      }}
                    >
                      Submit
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Container>
        </Box>
      </Grid>
    </Grid>
  );
};

export { AboutUs, AboutUsForm };
